import React, { useEffect, useState } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";

// Admin pages & layouts
import AdminDashboard from "../layouts/AdminDashboard.jsx";
import Candidates from "@/pages/admin/Candidates.jsx";
import CandidateDetail from "@/pages/admin/CandidateDetail.jsx";
import CandidateCreate from "@/pages/admin/candidates/CandidateCreate.jsx";
import AdminLogin from "@/pages/admin/AdminLogin.jsx";

// Public (Candidate) pages
import SignupForm from "../pages/public/SignupForm.jsx";
import AuthScreen from "@/pages/public/AuthScreen.jsx";
import ChangePasswordForm from "@/pages/public/ChangePasswordForm.jsx";
import LoginFlow from "@/layouts/LoginFlow.jsx";
import SkillsIndex from "@/pages/admin/skills/SkillsIndex.jsx";
import TermsIndex from "@/pages/admin/terms/TermsIndex.jsx";
import TermGroupsIndex from "@/pages/admin/terms/TermGroupsIndex.jsx";
import QuestionsIndex from "@/pages/admin/questions/QuestionsIndex.jsx";
import JobsIndex from "@/pages/admin/jobs/JobsIndex.jsx";
import JobShow from "@/pages/admin/jobs/JobShow.jsx";
import JobCreateForm from "@/pages/admin/jobs/JobCreateForm.jsx";
import VenuesIndex from "@/pages/admin/clients/VenuesIndex.jsx";
import VenueShow from "@/pages/admin/clients/VenueShow.jsx";
import TimesheetsIndex from "@/pages/admin/timesheets/TimesheetsIndex.jsx";
import CandidateAvailabilityIndex from "@/pages/admin/candidates/CandidateAvailabilityIndex.jsx";
import {CandidateProfileLandingPage} from "@/pages/candidate/profile/profile/CandidateProfileLandingPage.jsx";
import PersonalInfoForm from "@/pages/candidate/profile/profile/PersonalInfoForm.jsx";
import AgreementsPage from "@/pages/candidate/profile/profile/CandidateTerms.jsx";
import CvUploadForm from "@/pages/candidate/profile/profile/CvUploadForm.jsx";
import QuestionnaireForm from "@/pages/candidate/profile/profile/QuestionnaireForm.jsx";
import ComplianceForm from "@/pages/candidate/profile/profile/complianceForm.jsx";
import CandidateLayout from "@/layouts/CandidaiteLayout.jsx";

// Candidate auth wrapper
function PublicWrapper() {
    const location = useLocation();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    useEffect(() => {
        fetch("/api/v1/candidates/profile", {
            credentials: "include",
        })
            .then((res) => {
                setIsLoggedIn(res.ok);
                setIsCheckingAuth(false);
            })
            .catch(() => {
                setIsLoggedIn(false);
                setIsCheckingAuth(false);
            });
    }, []);

    if (isCheckingAuth) {
        return <div className="p-6 text-center text-sm">Checking session...</div>;
    }

    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route
                    path="/"
                    element={isLoggedIn ? <Navigate to="/candidate/profile" /> : <AuthScreen />}/>
                <Route
                    path="login"
                    element={
                        <AuthScreen/>
                    }
                />
                <Route
                    path="changepassword"
                    element={
                        <LoginFlow>
                            <ChangePasswordForm />
                        </LoginFlow>
                    }
                />
                {/* <Route path="/signup" element={<SignupForm />} /> */}

            </Routes>
        </AnimatePresence>
    );
}

function CandidateWrapper(){

    const location = useLocation();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    useEffect(() => {
        fetch("/api/v1/candidates/profile", {
            credentials: "include",
        })
            .then((res) => {
                setIsAuthenticated(res.ok);
                setIsCheckingAuth(false);
            })
            .catch(() => {
                setIsAuthenticated(false);
                setIsCheckingAuth(false);
            });
    }, []);

    if (isCheckingAuth) {
        return <div className="p-6 text-sm">Checking session...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} />;
    }

    return (
        <AnimatePresence mode={'wait'}>
            <Routes location={location} key={location.pathname}>
                <Route path="profile" element={<CandidateLayout><CandidateProfileLandingPage/></CandidateLayout>}/>
                <Route path="profile/personal" element={<CandidateLayout><PersonalInfoForm/></CandidateLayout>}/>
                <Route path="profile/compliance" element={<CandidateLayout><ComplianceForm/></CandidateLayout>}/>
                <Route path="profile/terms" element={<CandidateLayout><AgreementsPage/></CandidateLayout>}/>
                <Route path="profile/cv" element={<CandidateLayout><CvUploadForm/></CandidateLayout>}/>
                <Route path="profile/questions" element={<CandidateLayout><QuestionnaireForm/></CandidateLayout>}/>
            </Routes>

        </AnimatePresence>
    )
}

// Admin routes (with auth protection)
function AdminWrapper() {
    const location = useLocation();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    useEffect(() => {
        fetch("/api/v1/admin/check", { credentials: "include" })
            .then((res) => {
                setIsAuthenticated(res.ok);
                setIsCheckingAuth(false);
            })
            .catch(() => {
                setIsAuthenticated(false);
                setIsCheckingAuth(false);
            });
    }, []);

    if (isCheckingAuth) {
        return <div className="p-6 text-sm">Checking admin session...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/admin/login" state={{ from: location }} />;
    }

    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route index element={<AdminDashboard><Candidates /></AdminDashboard>} />
                <Route path="candidate/new" element={<AdminDashboard><CandidateCreate /></AdminDashboard>} />
                <Route path="candidate/:slug" element={<AdminDashboard><CandidateDetail /></AdminDashboard>} />
                <Route path="skills" element={<AdminDashboard><SkillsIndex/></AdminDashboard>}/>
                <Route path="terms" element={<AdminDashboard><TermsIndex/></AdminDashboard>}/>
                <Route path="term-groups" element={<AdminDashboard><TermGroupsIndex/></AdminDashboard>}/>
                <Route path='questions' element={<AdminDashboard><QuestionsIndex/></AdminDashboard>}/>
                <Route path='jobs' element={<AdminDashboard><JobsIndex/></AdminDashboard>}/>
                <Route path='jobs/new' element={<AdminDashboard><JobCreateForm/></AdminDashboard>}/>
                <Route path='jobs/:id' element={<AdminDashboard><JobShow/></AdminDashboard>}/>
                <Route path='venues' element={<AdminDashboard><VenuesIndex/></AdminDashboard>}/>
                <Route path='venues/:id' element={<AdminDashboard><VenueShow/></AdminDashboard>}/>
                <Route path='timesheets' element={<AdminDashboard><TimesheetsIndex/></AdminDashboard>}/>
                <Route path='candidate_availability' element={<AdminDashboard><CandidateAvailabilityIndex/></AdminDashboard>}/>
            </Routes>
        </AnimatePresence>
    );
}

// Main router
function Router() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/admin/login" element={<LoginFlow><AdminLogin /></LoginFlow>} />
                <Route path="/admin/*" element={<AdminWrapper />} />
                <Route path="/candidate/*" element={<CandidateWrapper/>}/>
                <Route path="*" element={<PublicWrapper />} />
            </Routes>
        </BrowserRouter>
    );
}

export default Router;
